import {EventEmitter, Injectable, PLATFORM_ID, Inject} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {Observable, of} from 'rxjs';
import {catchError, map, switchMap, tap} from 'rxjs/operators';
import {Router} from '@angular/router';
import {isPlatformBrowser} from '@angular/common';
import {CookieService} from 'ngx-cookie-service';
import {ObservableQueueService} from './observablequeue.service';
import { TranslationService } from '../../../services/translations.service';

@Injectable()
export class UserService {
  userUpdated: EventEmitter<any> = new EventEmitter();
  private user: { nickname: string, role: string };
  private readonly isBrowser: boolean = true;

  public constructor(
    private http: HttpClient,
    private router: Router,
    @Inject(PLATFORM_ID) platformId: string,
    private cookie: CookieService,
    private queue: ObservableQueueService,
    private translationService: TranslationService
  ) {
    this.isBrowser = isPlatformBrowser(platformId);
  }

  public loginEmail(email, orderToken, turnstileToken = null) {
    if (!this.isBrowser) {
      return of(null);
    }
    const method = 'email';
    return this.http.post('/auth/login', {
      method, email, orderToken, ...(turnstileToken ? { turnstileToken } : {})
    }).pipe(
      tap((response: any) => {
        if (response.success) {
          this.userUpdated.emit(response.user);
          this.user = response.user;
          this.setRememberToken(response.rememberToken);
        }
      })
    );
  }

  public loginExternal(accessToken, state, method) {
    if (!this.isBrowser) {
      return of(null);
    }
    return this.http.post('/auth/login', {
      accessToken, state, method
    }).pipe(
      tap((response: any) => {
        if (response.success) {
          this.userUpdated.emit(response.user);
          this.user = response.user;
          this.setRememberToken(response.rememberToken);
        }
      })
    );
  }

  public login(username, password, remember, token = null, turnstileToken = null): Observable<any> {
    if (!this.isBrowser) {
      return of(null);
    }
    return this.http.post(token ? '/auth/login/' + token : '/auth/login', {username, password, remember: true, ...(turnstileToken ? { turnstileToken } : {})}).pipe(
      tap((val: any) => {
        if (val.user) {
          this.userUpdated.emit(val.user);
          this.user = val.user;
          this.setRememberToken(val.rememberToken);
        }
      })
    );
  }

  public resetPassword(email): Observable<any> {
    if (!this.isBrowser) {
      return of(null);
    }
    return this.http.post('/auth/remind', {email}).pipe(
      tap((val: any) => {
      })
    );
  }

  public rememberLogin(): Observable<boolean> {
    if (!this.isBrowser || !this.cookie.check('gg-rtkn')) {
      return of(false);
    }

    return this.http.post('/auth/login', { rememberToken: this.cookie.get('gg-rtkn') }).pipe(
      map((val: any) => {
        if (val.user) {
          this.userUpdated.emit(val.user);
          this.user = val.user;
          this.setRememberToken(val.rememberToken);
          return true;
        }
        return false;
      }),
      catchError(() => of(false))
    );
  }

  public verify(token: string): Observable<any> {
    return this.http.post('/auth/verify', {token}).pipe(
      tap((val: any) => {
        if (val.rememberToken) {
          this.setRememberToken(val.rememberToken);
        }
      })
    );
  }

  public getOrderData(orderToken): Observable<any> {
    if (!this.isBrowser) {
      return of(null);
    }
    return this.http.post('/order-details', {orderToken}).pipe(
      tap((val: any) => {
        if (val.user) {
          this.userUpdated.emit(val.user);
          this.user = val.user;
          this.setRememberToken(val.rememberToken);
        }
      })
    );
  }

  public register(email, password, username): Observable<any> {
    if (!this.isBrowser) {
      return of(null);
    }
    const body = {email, password, username};
    return this.http.post('/auth/register', body).pipe(
      tap((val: any) => {
        if (val.user) {
          this.userUpdated.emit(val.user);
          this.user = val.user;
          this.setRememberToken(val.rememberToken);
        }
      })
    );
  }

  public logout(): void {
    if (!this.isBrowser) {
      return;
    }
    this.user = undefined;
    this.http.get('/auth/logout').subscribe(() => {
      this.cookie.delete('gg-rtkn', '/');
      this.userUpdated.emit({});

      let url = '/';
      if (this.cookie.check('gg-lang')) {
        const tmpLang = this.cookie.get('gg-lang');
        if (this.translationService.validLangs.includes(tmpLang) && tmpLang !== 'en') {
          url += tmpLang;
        }
      }

      window.location.href = url;
    });
  }

  public getUser(): Observable<any> {
    // Server... ignore, of(null) is observable
    if (!this.isBrowser) {
      return of(null);
    }

    if (this.user) {
      return of(this.user);
    }

    return this.queue.run('getUser', this.http.get('/auth/user',{withCredentials:true, headers:{'accept':'text/json'}}).pipe(
      switchMap((val: any) => {
        if (val) {
          this.user = val;
          this.userUpdated.emit(val);
          return of(val);
        }
        return this.rememberLogin().pipe(map(success => success ? this.user : null));
        // else {
        //   // checks if there is a remember token and tries to log in with it
        //   this.rememberLogin().subscribe();
        // }
      })
    ));
  }

  // public getUser(): Observable<any> {
  //   // Server... ignore, of(null) is observable
  //   if (!this.isBrowser) {
  //     return of(null);
  //   }

  //   if (this.user) {
  //     return of(this.user);
  //   }

  //   return this.queue.run('getUser', this.http.get('/auth/user',{withCredentials:true, headers:{'accept':'text/json'}}).pipe(
  //     tap((val: any) => {
  //       if (val) {
  //         this.user = val;
  //         this.userUpdated.emit(val);
  //       } else {
  //         // checks if there is a remember token and tries to log in with it
  //         this.rememberLogin().subscribe();
  //       }
  //     })
  //   ));
  // }

  public getUserSync(): any {
    return this.user;
  }

  private setRememberToken(token: string) {
    if (token) {
      this.cookie.set('gg-rtkn', token, 3650, '/');
    }
  }
}